import { useState, useEffect, useCallback } from 'react'
import { i18n } from '@fs/zion-locale'
import { useUser } from '@fs/zion-user'
import Cache from '@fs/zion-cache'
import { SESSION_CONFIG } from '../config/session-config'

const CHATBOT_CACHE_KEY = 'inbentaChatbotSession'

/**
 * Hook to pre-check the chatbot session cache before the overlay opens.
 * This allows us to determine immediately what screen to show when the user clicks the launch button.
 *
 * @returns {Object} Precheck state:
 *   - isLoading: true while checking the cache
 *   - hasValidSession: true if there's a non-expired session with messages
 *   - hasLanguageMismatch: true if the cached session language differs from current page language
 *   - previousLanguage: the language code of the cached session (if mismatch)
 *   - currentLanguage: the current page language code (if mismatch)
 *   - cachedSession: the cached session data (for restoration)
 *   - recheckSession: function to manually recheck the session (called when button is clicked)
 */
export default function useChatbotSessionPreCheck() {
  const { signedIn } = useUser()
  const [preCheckState, setPreCheckState] = useState({
    isLoading: true,
    hasValidSession: false,
    hasLanguageMismatch: false,
    previousLanguage: null,
    currentLanguage: null,
    cachedSession: null,
  })

  const checkSession = useCallback(async () => {
    try {
      // Use the same cache configuration as chatbotSessionCache.js
      const cache = new Cache({
        store: 'indexeddb',
        dbName: 'fs',
        storeName: 'inbentaChatbot',
        encrypted: true,
      })

      const cached = await cache.getItem(CHATBOT_CACHE_KEY)

      if (!cached || !cached.sessionId || !Array.isArray(cached.messages)) {
        // No valid cache exists
        setPreCheckState({
          isLoading: false,
          hasValidSession: false,
          hasLanguageMismatch: false,
          previousLanguage: null,
          currentLanguage: null,
          cachedSession: null,
        })
        return
      }

      // Check if session is expired
      const timeoutMs = signedIn ? SESSION_CONFIG.SIGNED_IN_TIMEOUT_MS : SESSION_CONFIG.GUEST_TIMEOUT_MS
      const isExpired = !cached.savedAt || Date.now() - cached.savedAt > timeoutMs

      if (isExpired) {
        // Session is expired
        setPreCheckState({
          isLoading: false,
          hasValidSession: false,
          hasLanguageMismatch: false,
          previousLanguage: null,
          currentLanguage: null,
          cachedSession: null,
        })
        return
      }

      // Check for language mismatch
      const currentLanguage = i18n.language
      const hasLanguageMismatch = Boolean(cached.language && cached.language !== currentLanguage)

      // Check if there are user messages (we only show mismatch notification if user has messages)
      const hasUserMessages = cached.messages?.some((msg) => msg.from && msg.from.toLowerCase().trim() === 'user')

      const finalState = {
        isLoading: false,
        hasValidSession: true,
        hasLanguageMismatch: hasLanguageMismatch && hasUserMessages,
        previousLanguage: hasLanguageMismatch ? cached.language : null,
        currentLanguage: hasLanguageMismatch ? currentLanguage : null,
        cachedSession: cached,
      }

      setPreCheckState(finalState)
    } catch (error) {
      // On error, assume no valid session
      console.error('Error checking chatbot session cache:', error)
      setPreCheckState({
        isLoading: false,
        hasValidSession: false,
        hasLanguageMismatch: false,
        previousLanguage: null,
        currentLanguage: null,
        cachedSession: null,
      })
    }
  }, [signedIn])

  // Check session on mount and when signedIn changes
  useEffect(() => {
    checkSession()
  }, [checkSession])

  // Function to recheck the session (called when button is clicked to get fresh timestamp check)
  const recheckSession = useCallback(async () => {
    setPreCheckState((prev) => ({ ...prev, isLoading: true }))
    await checkSession()
  }, [checkSession])

  return {
    ...preCheckState,
    recheckSession,
  }
}
